import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signInWithEmailAndPassword, signOut, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

export interface UserProfile {
  ownerID: string;
  masterOwnerID: string;
  role: 'Admin' | 'Tech' | 'Clerk';
  name: string;
  email: string;
  assignedSites?: string[];
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  announcements?: boolean;
  lastLogin?: any;
  createdAt?: any;
  notificationTimeZone?: string;
  subscriptionPlan?: any;
  /** Admin-set default sweep route team # until the user saves their own team on a device. */
  defaultSweepTeamNumber?: number | null;
}

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, userData: any) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  updateUserProfile: (profile: Partial<UserProfile>) => void;
  /** Call right after Firestore `users/{uid}` write during signup so profile exists before auth listener finishes retrying. */
  hydrateUserProfileAfterSignup: (profile: UserProfile) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Fetch user profile
    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('User profile not found');
    }

    const userData = docSnap.data() as UserProfile;

    // Fetch company subscription plan
    const companyRef = doc(db, 'companies', userData.masterOwnerID);
    const companySnap = await getDoc(companyRef);

    const profile = {
      ...userData,
      subscriptionPlan: companySnap.data()?.subscriptionPlan,
    };

    setUserProfile(profile);
    localStorage.setItem('userProfile', JSON.stringify(profile));
  };

  const register = async (email: string, password: string, userData: any) => {
    // Note: This function is deprecated for new registrations.
    // New multi-step registration is handled directly in RegisterSummary.tsx
    // This is kept for backward compatibility only.
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create user profile in Firestore
    const userProfile = {
      ownerID: user.uid,
      masterOwnerID: user.uid, // For admin registration, they are their own master
      role: 'Admin' as const,
      name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
      email: email,
      assignedSites: [],
      announcements: true,
      ...userData,
    };

    await setDoc(doc(db, 'users', user.uid), userProfile);
    setUserProfile(userProfile);
  };

  const logout = async () => {
    await signOut(auth);
    setUserProfile(null);
    localStorage.removeItem('userProfile');
  };

  const updateUserProfile = (profile: Partial<UserProfile>) => {
    if (userProfile) {
      const updatedProfile = { ...userProfile, ...profile };
      setUserProfile(updatedProfile);
      localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
    }
  };

  const hydrateUserProfileAfterSignup = React.useCallback((profile: UserProfile) => {
    setUserProfile(profile);
    try {
      localStorage.setItem('userProfile', JSON.stringify(profile));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    /** Registration writes `users/{uid}` right after `createUser`; listener may run before `setDoc` completes. */
    const readCachedProfileForUid = (uid: string): UserProfile | null => {
      try {
        const raw = localStorage.getItem('userProfile');
        if (!raw) return null;
        const parsed = JSON.parse(raw) as UserProfile;
        if (parsed.ownerID === uid || parsed.masterOwnerID === uid) {
          return parsed;
        }
      } catch {
        /* ignore */
      }
      return null;
    };

    const mergeCompanyPlan = async (userData: UserProfile): Promise<UserProfile> => {
      try {
        const companyRef = doc(db, 'companies', userData.masterOwnerID);
        const companySnap = await getDoc(companyRef);
        if (companySnap.exists()) {
          return {
            ...userData,
            subscriptionPlan: companySnap.data()?.subscriptionPlan ?? userData.subscriptionPlan,
          };
        }
      } catch {
        /* ignore */
      }
      return userData;
    };

    const loadProfileFromFirestore = async (uid: string): Promise<UserProfile | null> => {
      const maxAttempts = 25;
      const delayMs = 250;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const cached = readCachedProfileForUid(uid);
        if (cached) {
          return mergeCompanyPlan(cached);
        }

        try {
          const docRef = doc(db, 'users', uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const userData = docSnap.data() as UserProfile;
            return mergeCompanyPlan(userData);
          }
        } catch (err) {
          console.warn('[AuthContext] getDoc users failed (will retry):', err);
        }

        if (attempt < maxAttempts - 1) {
          await new Promise((r) => setTimeout(r, delayMs));
        }
      }
      return null;
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const savedRaw = localStorage.getItem('userProfile');
        let usedCache = false;
        if (savedRaw) {
          try {
            const parsed = JSON.parse(savedRaw) as UserProfile;
            const matchesUid =
              parsed.ownerID === user.uid ||
              parsed.masterOwnerID === user.uid;
            if (matchesUid) {
              setUserProfile(parsed);
              usedCache = true;
            }
          } catch {
            localStorage.removeItem('userProfile');
          }
        }

        if (!usedCache) {
          const profile = await loadProfileFromFirestore(user.uid);
          if (profile) {
            setUserProfile(profile);
            localStorage.setItem('userProfile', JSON.stringify(profile));
          } else {
            console.warn(
              '[AuthContext] No user profile for UID after signup retries (check Firestore rules and network):',
              user.uid
            );
            setUserProfile(null);
          }
        }
      } else {
        setUserProfile(null);
        localStorage.removeItem('userProfile');
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    login,
    register,
    logout,
    loading,
    updateUserProfile,
    hydrateUserProfileAfterSignup,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
